import { ReactNode } from 'react'
import styled from '@emotion/styled'
import { FusionColor } from "../../../../theme/FusionStyle"
import { V2L1ReasonableButton } from "../../Button/V2L1ReasonableButton"
import { V2L2ModalContent, V2L2ModalDetail } from "../V2ModalContent"
import { V2L2ModalActionbar } from "../V2L2ModalActionbar"
import { V2L2SmallModalRoot } from "../V2L2ModalRoot"
import { V2L2ModalTitlebar, V2L1ModalDismissButton } from "../V2ModalTitlebar"
import { LocationStatus, StatusIndicatorContext, locationIndicatorProps } from '../../Indicator/StatusIndicatorProps'
import { V2L1StatusIndicator } from '../../Indicator/V2L1StatusIndicator'
import { plainDateTimeSecs } from '@fusion-advantage/formlib'
import { DateTime } from 'luxon'
import { faArrowsRotate, faLocationArrow } from '@fortawesome/pro-solid-svg-icons'

interface V2L3LocationModalProps {
	status: LocationStatus|null
	lastUpdatedAt: DateTime
	canSkip: boolean
	onRefreshClick?: React.MouseEventHandler<HTMLButtonElement>
	onSkipClick?: React.MouseEventHandler<HTMLButtonElement>
	onRequestAccessClick?: React.MouseEventHandler<HTMLButtonElement>
	hideDismiss?: boolean
	onDismissClick?: React.MouseEventHandler<HTMLButtonElement>
}

export const V2L3LocationModal = ({status, lastUpdatedAt, canSkip, onRefreshClick, onSkipClick, onRequestAccessClick, hideDismiss, onDismissClick}: V2L3LocationModalProps) => {
	return (
		<V2L2SmallModalRoot>
			<V2L2ModalTitlebar
				title="Location"
				buttons={hideDismiss ? null : <V2L1ModalDismissButton onClick={onDismissClick} />}
			/>
			<V2L2ModalContent upper columns>
				<V2L2ModalDetail upper title="Permissions">
					{status ? (
						<V2L1StatusIndicator {...locationIndicatorProps(status, StatusIndicatorContext.Modal)} nowrap />
					) : '—'}
				</V2L2ModalDetail>
				<V2L2ModalDetail upper title="Last Updated At">
					{plainDateTimeSecs(lastUpdatedAt)}
				</V2L2ModalDetail>
			</V2L2ModalContent>
			<Explainer status={status} canSkip={canSkip} />
			<ActionBar
				status={status}
				canSkip={canSkip}
				onRefreshClick={onRefreshClick}
				onSkipClick={onSkipClick}
				onRequestAccessClick={onRequestAccessClick}
			/>
		</V2L2SmallModalRoot>
	)
}

const RationaleContent = () => {
	return (
		<p>
			Welcome to Fusion! As a condition of your engagement with Fusion Advantage,
			you are required to allow us to access and record your location when starting
			or performing a patient encounter, and while making changes to encounter data.
			This data is utilized for patient safety and fraud prevention purposes.
		</p>
	)
}

const ExplainerRoot = styled.div({
	fontWeight: 'normal',
	gridColumn: 'span 2',
	marginBlock: '-1rem',
	'p': {
		marginBlock: '1rem',
	},
})

interface SubcomponentProps {
	status: LocationStatus|null
	canSkip?: boolean
	onRefreshClick?: React.MouseEventHandler<HTMLButtonElement>
	onRequestAccessClick?: React.MouseEventHandler<HTMLButtonElement>
	onSkipClick?: React.MouseEventHandler<HTMLButtonElement>
}

const Explainer = ({status}: SubcomponentProps) => {
	let content: ReactNode = null
	switch(status) {
		case LocationStatus.NoBrowserSupport:
			content = (
				<ExplainerRoot>
					<RationaleContent />
					<p>
						Unfortunately, Location Services is not supported in this browser.  Please
						use an alternate browser, or contact support for assistance.
					</p>
				</ExplainerRoot>
			)
			break
		case LocationStatus.ServicesDisabled:
			content = (
				<ExplainerRoot>
					<RationaleContent />
					<p>
						Unfortunately, Location Services has been disabled on your device.  Please
						enable Location Services to continue, or contact support for assistance.
					</p>
				</ExplainerRoot>
			)
			break
		case LocationStatus.Denied:
			content = (
				<ExplainerRoot>
					<RationaleContent />
					<p>
						Unfortunately, access to your location on this app has been denied.  Please
						grant location access to this app, or contact support for assistance.
					</p>
				</ExplainerRoot>
			)
			break
		case LocationStatus.Prompt:
			content = (
				<ExplainerRoot>
					<RationaleContent />
					<p>
						Tap<b> "Allow While Using App" </b>on the following dialog to continue using
						the application. If you have any questions about this requirement, please
						contact clinician field support.
					</p>
				</ExplainerRoot>
			)
			break
		case LocationStatus.Unknown:
			content = (
				<ExplainerRoot>
					<p>
						Sorry, an unknown error occurred; please try again or contact support.
					</p>
				</ExplainerRoot>
			)
	}
	if(!content) {
		return null
	}
	return (
		<V2L2ModalContent>
			<V2L2ModalDetail title="Notice">
				{content}
			</V2L2ModalDetail>
		</V2L2ModalContent>
	)
}

const ActionBar = ({status, canSkip, onRefreshClick, onSkipClick, onRequestAccessClick}: SubcomponentProps) => {
	let actions: ReactNode[] = []
	switch(status) {
		case LocationStatus.NoBrowserSupport:
		case LocationStatus.ServicesDisabled:
		case LocationStatus.Denied:
		case LocationStatus.Unknown:
			// really, a restart of the app is required.
			// actions.push(
			// 	<V2L1ReasonableButton
			// 		key="retry"
			// 		color={FusionColor.BlueMain}
			// 		icon={faArrowsRotate}
			// 		label="Retry"
			// 		onClick={onRetryClick}
			// 	/>
			// )
			if(canSkip) {
				actions.push(
					<V2L1ReasonableButton
						key="skip"
						color={FusionColor.OrangeMain}
						label="[Dev Only] Skip"
						onClick={onSkipClick}
					/>
				)
			}
			break
		case LocationStatus.Prompt:
			actions.push(
				<V2L1ReasonableButton
					color={FusionColor.BlueMain}
					icon={faLocationArrow}
					label="Request Access"
					onClick={onRequestAccessClick}
				/>
			)
			break
		case LocationStatus.Success:
			actions.push(
				<V2L1ReasonableButton
					color={FusionColor.BlueMain}
					icon={faArrowsRotate}
					label="Refresh"
					onClick={onRefreshClick}
				/>
			)
			break
		case null:
			// there was a error state in which the location was never being
			// activated—  it shouldn't happen in the future, but in case it
			// does, this would be a backup way to kickstart it
			actions.push(
				<V2L1ReasonableButton
					key="retry"
					color={FusionColor.BlueMain}
					icon={faArrowsRotate}
					label="Retry"
					onClick={onRefreshClick}
				/>
			)
	}
	if(actions.length < 1) {
		return null
	}
	return (
		<V2L2ModalActionbar>
			{actions}
		</V2L2ModalActionbar>
	)
}
